// CalendarConnections.js - Calendar Integration Settings Page

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, Chip, CircularProgress, Alert,
  Stack, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  RadioGroup, Radio, FormControlLabel, FormControl, FormLabel, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
  Sync as SyncIcon,
  LinkOff as DisconnectIcon,
  Add as AddIcon,
  Google as GoogleIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { calendarService } from './activityCommService';
import {
  CalendarProviderLabels, CalendarProviderOptions,
  CalendarSyncStatusLabels, CalendarSyncStatusColors,
  formatDateTime
} from './activityCommEnums';

const CalendarConnections = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('GOOGLE');
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [confirmDisconnect, setConfirmDisconnect] = useState(null);

  useEffect(() => {
    document.title = 'Calendar Connections | Activity & Communications';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadConnections();
    }
  }, [canView]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) setLoading(false);
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadConnections = async () => {
    setLoading(true);
    try {
      const res = await calendarService.getConnections();
      setConnections(res.data || res || []);
    } catch (err) {
      enqueueSnackbar('Failed to load calendar connections', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await calendarService.connect({ provider: selectedProvider });
      enqueueSnackbar('Calendar connected successfully', { variant: 'success' });
      setConnectOpen(false);
      if (res.oauth_url) {
        window.open(res.oauth_url, '_blank');
      }
      loadConnections();
    } catch (err) {
      enqueueSnackbar('Failed to connect calendar', { variant: 'error' });
      console.error(err);
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async (connectionId) => {
    setSyncing(prev => ({ ...prev, [connectionId]: true }));
    try {
      await calendarService.sync(connectionId);
      enqueueSnackbar('Calendar sync started', { variant: 'success' });
      loadConnections();
    } catch (err) {
      enqueueSnackbar('Failed to sync calendar', { variant: 'error' });
      console.error(err);
    } finally {
      setSyncing(prev => ({ ...prev, [connectionId]: false }));
    }
  };

  const handleDisconnect = async (connectionId) => {
    try {
      await calendarService.disconnect(connectionId);
      enqueueSnackbar('Calendar disconnected', { variant: 'success' });
      setConfirmDisconnect(null);
      loadConnections();
    } catch (err) {
      enqueueSnackbar('Failed to disconnect calendar', { variant: 'error' });
      console.error(err);
    }
  };

  const getProviderIcon = (provider) => {
    if (provider === 'GOOGLE') return <GoogleIcon sx={{ color: '#4285f4' }} />;
    return <SettingsIcon sx={{ color: '#0078d4' }} />;
  };

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view this page.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/calendar`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Calendar Connections
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your calendar integrations
            </Typography>
          </Box>
        </Box>
        {canModify && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setConnectOpen(true)}>
            Connect Calendar
          </Button>
        )}
      </Box>

      {/* Info Alert */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        <strong>Two-Way Sync</strong> - Connected calendars support two-way synchronization.
        Events created here will appear in your external calendar, and events created in your
        external calendar will sync back to this platform.
      </Alert>

      {/* Connections List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (Array.isArray(connections) && connections.length > 0) ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Calendar ID</TableCell>
                <TableCell>Last Synced</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {connections.map((conn) => (
                <TableRow key={conn.id}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {getProviderIcon(conn.provider)}
                      <Typography variant="body2" fontWeight={600}>
                        {CalendarProviderLabels[conn.provider] || conn.provider}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={CalendarSyncStatusLabels[conn.status] || conn.status}
                      color={CalendarSyncStatusColors[conn.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {conn.calendar_id || conn.email || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {conn.last_synced ? formatDateTime(conn.last_synced) : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={syncing[conn.id] ? <CircularProgress size={14} /> : <SyncIcon />}
                        onClick={() => handleSync(conn.id)}
                        disabled={syncing[conn.id]}
                      >
                        Sync
                      </Button>
                      {canModify && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DisconnectIcon />}
                          onClick={() => setConfirmDisconnect(conn.id)}
                        >
                          Disconnect
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <SettingsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No calendar connections</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect your Google Calendar or Microsoft Outlook to sync events.
          </Typography>
          {canModify && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setConnectOpen(true)}>
              Connect Calendar
            </Button>
          )}
        </Paper>
      )}

      {/* Connect Dialog */}
      <Dialog open={connectOpen} onClose={() => setConnectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Connect Calendar</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ mt: 1 }}>
            <FormLabel component="legend">Select Provider</FormLabel>
            <RadioGroup
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
            >
              {CalendarProviderOptions.map(opt => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio />}
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {getProviderIcon(opt.value)}
                      <Typography>{opt.label}</Typography>
                    </Stack>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConnect} disabled={connecting}>
            {connecting ? 'Connecting...' : 'Connect'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Disconnect Dialog */}
      <Dialog open={!!confirmDisconnect} onClose={() => setConfirmDisconnect(null)}>
        <DialogTitle>Disconnect Calendar</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to disconnect this calendar? Synced events will remain but
            no new events will be synchronized.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDisconnect(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDisconnect(confirmDisconnect)}
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarConnections;
